<template>
  <div class="p-6">
    <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
      {{ monitor?.id ? 'Edit Monitor' : 'New Monitor' }}
    </h2>

    <form @submit.prevent="save" class="space-y-2">
      <UiControl id="monitor-name" label="Name" :errors="fieldErrors.name">
        <UiInput id="monitor-name" v-model="form.name" placeholder="Phoenix Radar" />
      </UiControl>

      <div class="grid grid-cols-2 gap-3">
        <UiControl id="monitor-metro-city" label="Metro City" :errors="fieldErrors.metro_city">
          <UiInput id="monitor-metro-city" v-model="form.metro_city" placeholder="Phoenix" />
        </UiControl>
        <UiControl id="monitor-metro-state" label="State" :errors="fieldErrors.metro_state">
          <UiInput id="monitor-metro-state" v-model="form.metro_state" placeholder="AZ" />
        </UiControl>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <UiControl id="monitor-frequency" label="Frequency" :errors="fieldErrors.frequency">
          <UiSelect id="monitor-frequency" v-model="form.frequency" :options="frequencyOptions" />
        </UiControl>
        <UiControl
          id="monitor-min-relevance" label="Minimum Relevance"
          tooltip-text="Findings scored below this (0–100) are discarded. Lower it to capture more general niche chatter; raise it for hot leads only."
          :errors="fieldErrors.min_relevance">
          <UiInput id="monitor-min-relevance" v-model="form.min_relevance" type="number" min="0" max="100" />
        </UiControl>
      </div>

      <UiControl
        id="monitor-platforms" label="Platforms"
        tooltip-text="Facebook groups and Nextdoor are mostly private; coverage there is best-effort via publicly indexed posts.">
        <div class="flex gap-4 py-1">
          <label v-for="platform in allPlatforms" :key="platform" class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              class="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              :checked="form.platforms.includes(platform)"
              @change="togglePlatform(platform)">
            <span class="capitalize">{{ platform }}</span>
          </label>
        </div>
      </UiControl>

      <UiControl
        id="monitor-topics" label="Topics"
        tooltip-text="Search phrases the way locals post them. The metro is appended automatically."
        :errors="fieldErrors.topics">
        <div class="space-y-2">
          <div class="flex flex-wrap gap-2" v-if="form.topics.length">
            <span
              v-for="(topic, index) in form.topics" :key="`${topic}-${index}`"
              class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-xs">
              {{ topic }}
              <button type="button" class="hover:text-red-500" @click="form.topics.splice(index, 1)">
                <i class="far fa-times" />
              </button>
            </span>
          </div>
          <div class="flex gap-2">
            <UiInput
              id="monitor-new-topic" v-model="newTopic"
              placeholder="e.g. plumber recommendation"
              @keydown.enter.prevent="addTopic" />
            <button type="button" class="btn btn-secondary btn-sm shrink-0" @click="addTopic">
              Add
            </button>
            <button
              type="button"
              class="btn btn-secondary btn-sm shrink-0"
              :disabled="suggesting || !form.metro_city"
              v-tooltip="'Generate topics with AI from the business profile and metro.'"
              @click="suggest">
              <i :class="suggesting ? 'far fa-spinner fa-spin' : 'far fa-magic'" class="mr-1" />
              {{ suggesting ? 'Suggesting...' : 'Suggest topics' }}
            </button>
          </div>
        </div>
      </UiControl>

      <UiControl
        id="monitor-slack" label="Slack Webhook URL (optional)"
        tooltip-text="Incoming webhook for the channel that should get new-findings alerts."
        :errors="fieldErrors.slack_webhook_url">
        <UiInput id="monitor-slack" v-model="form.slack_webhook_url" placeholder="https://hooks.slack.com/services/..." />
      </UiControl>

      <div v-if="error" class="rounded-md bg-red-50 dark:bg-red-900/20 p-3">
        <p class="text-sm text-red-700 dark:text-red-400">
          {{ error }}
        </p>
      </div>

      <div class="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
        <button type="button" class="btn btn-secondary btn-sm" @click="$emit('on-close-panel')">
          Cancel
        </button>
        <button type="submit" :disabled="saving" class="btn btn-primary btn-sm">
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import UiInput from '~/javascript/components/ui/form/input/input.vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'
import UiSelect from '~/javascript/components/ui/form/select/select.vue'
import vTooltip from '~/javascript/directives/tooltip'
import { toastError } from '~/javascript/utils/toast'
import { createSocialMonitor, updateSocialMonitor, suggestTopics } from '~/javascript/api/social_monitors'

const props = defineProps({
  businessId: { type: String, required: true },
  monitor: { type: Object, default: null },
  defaultCity: { type: String, default: '' },
  defaultState: { type: String, default: '' }
})

const emit = defineEmits(['saved', 'on-close-panel'])

const allPlatforms = ['reddit', 'facebook', 'nextdoor']

const frequencyOptions = [
  { id: 'daily', title: 'Daily' },
  { id: 'every_3_days', title: 'Every 3 Days' },
  { id: 'weekly', title: 'Weekly' }
]

const form = reactive({
  name: props.monitor?.name || '',
  metro_city: props.monitor?.metro_city || props.defaultCity,
  metro_state: props.monitor?.metro_state || props.defaultState,
  frequency: props.monitor?.frequency || 'weekly',
  platforms: [...(props.monitor?.platforms || allPlatforms)],
  topics: [...(props.monitor?.topics || [])],
  slack_webhook_url: props.monitor?.slack_webhook_url || '',
  min_relevance: props.monitor?.min_relevance ?? 30
})

const newTopic = ref('')
const saving = ref(false)
const suggesting = ref(false)
const error = ref(null)
const fieldErrors = ref({})

const togglePlatform = (platform) => {
  const index = form.platforms.indexOf(platform)
  if (index >= 0) form.platforms.splice(index, 1)
  else form.platforms.push(platform)
}

const addTopic = () => {
  const topic = newTopic.value.trim()
  if (topic && !form.topics.includes(topic)) form.topics.push(topic)
  newTopic.value = ''
}

const suggest = async() => {
  suggesting.value = true
  try {
    const { data } = await suggestTopics(props.businessId, {
      metro_city: form.metro_city,
      metro_state: form.metro_state
    })
    data.topics.forEach((topic) => {
      if (!form.topics.includes(topic)) form.topics.push(topic)
    })
  } catch (e) {
    toastError(new Error(e.response?.data?.message || 'Failed to suggest topics'))
  } finally {
    suggesting.value = false
  }
}

const save = async() => {
  saving.value = true
  error.value = null
  fieldErrors.value = {}
  try {
    if (props.monitor?.id) {
      await updateSocialMonitor(props.monitor.id, form)
    } else {
      await createSocialMonitor(props.businessId, form)
    }
    emit('saved')
  } catch (e) {
    const details = e.response?.data?.details
    if (details) fieldErrors.value = details
    error.value = e.response?.data?.message || 'Failed to save monitor'
  } finally {
    saving.value = false
  }
}
</script>
